import { zodResolver } from "@hookform/resolvers/zod";
import {
  Modal,
  RichTextField,
  SelectField,
  TextInputField,
} from "@twinkltech/curriculum-architect-component-library";
import { Curriculum } from "@twinkltech/curriculum-architect-domain-hooks";
import { Button, IconCommunicationInformation, Text } from "@twinkltech/web-ui";
import React, { useEffect, useId, useMemo, useState } from "react";
import { SubmitHandler, useController, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { TableData } from "@/states/unitTableReducer/unitTableReducer";

import { COMMON_NS } from "@/i18n/common/common.i18n";
import { useFeedback } from "@/providers/Feedback/Feedback.context";

import { GENERIC_CURRICULUM_FORM_NS } from "../GenericCurriculumModal/GenericCurriculumModal.i18n";

import { REQUIRED_FIELD_MESSAGE } from "@/utils/validation-messages";

import { useIsOptionDisabled } from "./utils/useIsOptionDisabled/useIsOptionDisabled";

const CustomUnitFormSchema = z.object({
  title: z.string().min(1, REQUIRED_FIELD_MESSAGE).max(120),
  description: z.string().max(2000).optional(),
  cohort: z.string().min(1, REQUIRED_FIELD_MESSAGE),
  startOnColumn: z.string().min(1, REQUIRED_FIELD_MESSAGE),
  endOnColumn: z.string().min(1, REQUIRED_FIELD_MESSAGE),
});

type CustomUnitFormData = z.infer<typeof CustomUnitFormSchema>;

export const CustomUnitCreateModal = ({
  customUnit,
  loading = false,
  onClose,
  onSubmit,
  tableData,
  title,
  ...props
}: {
  customUnit?: {
    id: string;
    title: string;
    description?: string;
    startOnColumn: string;
    endOnColumn: string;
    cohort: string;
  };
  open: boolean;
  onClose: () => void;
  title: string;
  loading?: boolean;
  error?: Error | null;
  onSubmit: (data: {
    id?: string;
    row: string;
    column: string;
    span?: number;
    title: string;
    description?: string;
  }) => void;
  curriculum?: Curriculum | null;
  tableData: Pick<TableData, "cells" | "rows" | "columns">;
} & Omit<
  React.ComponentProps<typeof Modal>,
  "open" | "onClose" | "children" | "onSubmit"
>) => {
  const { t } = useTranslation(GENERIC_CURRICULUM_FORM_NS);
  const { incrementEngagementCount } = useFeedback();

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Accessibility IDs
  const formId = useId();
  const currNameInputId = useId();
  const cohortInputId = useId();
  const startOnColumnInputId = useId();
  const endOnColumnInputId = useId();

  // Get the ordered columns for consistency with EditSubjectPlan
  const orderedColumns = useMemo(
    () => [...tableData.columns].sort((a, b) => a.order - b.order),
    [tableData.columns]
  );

  const formDefaultValues: CustomUnitFormData = {
    title: customUnit?.title || "",
    description: customUnit?.description || "",
    cohort: customUnit?.cohort || "",
    startOnColumn: customUnit?.startOnColumn || "",
    endOnColumn: customUnit?.endOnColumn || "",
  };

  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    reset,
    setValue,
    watch,
  } = useForm<CustomUnitFormData, unknown, CustomUnitFormData>({
    resolver: zodResolver<CustomUnitFormData, unknown, CustomUnitFormData>(
      CustomUnitFormSchema
    ),
    defaultValues: formDefaultValues,
  });

  // When the cohort is changed from its default value, set the start and end values back to empty
  const watchedCohort = watch("cohort");

  useEffect(() => {
    // if all values match their defaults, do nothing
    if (
      watchedCohort === formDefaultValues.cohort &&
      watch("startOnColumn") === formDefaultValues.startOnColumn &&
      watch("endOnColumn") === formDefaultValues.endOnColumn
    ) {
      return;
    }

    // Reset start and end values when cohort changes
    setValue("startOnColumn", "");
    setValue("endOnColumn", "");
  }, [
    formDefaultValues.cohort,
    formDefaultValues.endOnColumn,
    formDefaultValues.startOnColumn,
    setValue,
    watch,
    watchedCohort,
  ]);

  // When the start is changed from its default value, set the end value back to empty
  const watchedStart = watch("startOnColumn");
  useEffect(() => {
    // if all values match their defaults, do nothing
    if (
      watchedStart === formDefaultValues.startOnColumn &&
      watch("endOnColumn") === formDefaultValues.endOnColumn
    ) {
      return;
    }

    // Reset start and end values when cohort changes
    setValue("endOnColumn", "");
  }, [
    watchedStart,
    formDefaultValues.endOnColumn,
    formDefaultValues.startOnColumn,
    setValue,
    watch,
  ]);

  const { field: descriptionField } = useController({
    control,
    name: "description",
  });

  // Note: Need to use a controller for the Headless UI custom components
  const { field: cohortField } = useController({
    control,
    name: "cohort",
    defaultValue: customUnit?.cohort || "",
  });

  const { field: startOnColumnField } = useController({
    control,
    name: "startOnColumn",
    defaultValue: customUnit?.startOnColumn || "",
  });

  const { field: endOnColumnField } = useController({
    control,
    name: "endOnColumn",
    defaultValue: customUnit?.endOnColumn || "",
  });

  // used to disabled dropdowns until pre-requisites are selected
  const isCohortSelected = cohortField.value !== "";
  const isStartSelected = startOnColumnField.value !== "";

  const { isEndOnColumnDisabled, isStartColumnDisabled } = useIsOptionDisabled({
    customUnit: customUnit
      ? {
          cohort: customUnit.cohort,
          startOnColumn: customUnit.startOnColumn,
          endOnColumn: customUnit.endOnColumn,
        }
      : undefined,
    formValues: {
      cohort: cohortField.value,
      startOnColumn: startOnColumnField.value,
      endOnColumn: endOnColumnField.value,
    },
    tableData,
  });

  const onHandleOnClose = () => {
    reset();
    onClose();
  };

  const onFormSubmit: SubmitHandler<CustomUnitFormData> = (data) => {
    const columnId = orderedColumns.find(
      (col) => col.id === data.startOnColumn
    )?.id;

    if (!columnId) {
      return;
    }

    incrementEngagementCount();

    const payload: {
      id?: string;
      row: string;
      column: string;
      span?: number;
      title: string;
      description?: string;
    } = {
      row: data.cohort,
      column: columnId,
      span:
        orderedColumns.findIndex((col) => col.id === data.endOnColumn) -
          orderedColumns.findIndex((col) => col.id === data.startOnColumn) +
          1 || 1,
      title: data.title,
      description: data.description,
    };

    if (customUnit) {
      payload.id = customUnit.id;
    }

    setIsSubmitting(true);
    onSubmit(payload);
    setIsSubmitting(false);
    onHandleOnClose();
  };

  return (
    <Modal
      hasCloseButton
      {...props}
      className="max-h-[95vh]"
      width="md"
      onClose={onHandleOnClose}
    >
      <Modal.Header>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="mb-2 overflow-y-auto">
        <form
          className="py-2 flex flex-col gap-4"
          id={formId}
          onSubmit={handleSubmit(onFormSubmit)}
        >
          <TextInputField
            className="mb-0!"
            id={currNameInputId}
            label="Title of Unit"
            placeholder={t("actions.enter-text", { ns: COMMON_NS })}
            {...register("title", { required: true })}
            errorText={errors.title?.message}
          />

          <RichTextField
            errorText={errors.description?.message}
            infoText="2000 character limit"
            label="Description"
            markdown={descriptionField.value || ""}
            onBlur={descriptionField.onBlur}
            onChange={descriptionField.onChange}
          />
          <SelectField
            className={cohortField.value === "" ? "text-neutral-600" : ""}
            id={cohortInputId}
            label="Cohort"
            options={[
              {
                value: "",
                label: "Select a cohort",
                disabled: true,
              },
              ...(tableData.rows.map((row) => ({
                value: row.id,
                label: row.title,
              })) || []),
            ]}
            {...cohortField}
            errorText={errors.cohort?.message}
          />
          <SelectField
            className={
              startOnColumnField.value === "" ? "text-neutral-600" : ""
            }
            disabled={!isCohortSelected}
            id={startOnColumnInputId}
            label="Start"
            options={[
              {
                value: "",
                label: "Select a start",
                disabled: true,
              },
              ...orderedColumns.map((col) => ({
                value: col.id,
                label: col.title,
                disabled: isStartColumnDisabled(col),
              })),
            ]}
            {...startOnColumnField}
            errorText={errors.startOnColumn?.message}
          />
          <SelectField
            className={endOnColumnField.value === "" ? "text-neutral-600" : ""}
            disabled={!isCohortSelected || !isStartSelected}
            id={endOnColumnInputId}
            label="Finish"
            options={[
              {
                value: "",
                label: "Select an end",
                disabled: true,
              },
              ...orderedColumns.map((col) => {
                return {
                  value: col.id,
                  label: col.title,
                  disabled: isEndOnColumnDisabled(col),
                };
              }),
            ]}
            {...endOnColumnField}
            errorText={errors.endOnColumn?.message}
          />
          <div className="flex items-center gap-4 bg-neutral-100 p-200 rounded-lg">
            <IconCommunicationInformation className="w-10" />
            <Text as="p" className="m-0! leading-tight" variant="base-medium">
              Currently, custom units cannot be duplicated, saved or reused.
              <br /> Use them as simple summaries of instances where you plan to
              use external content.
            </Text>
          </div>
        </form>
      </Modal.Body>
      <Modal.Footer className="gap-3">
        <Button type="button" variant="outlined" onClick={onHandleOnClose}>
          {t("actions.cancel", { ns: COMMON_NS })}
        </Button>
        <Button
          disabled={loading || !open || isSubmitting}
          form={formId}
          variant="primary"
        >
          {customUnit
            ? t("actions.apply-changes", { ns: COMMON_NS })
            : "Add Custom Unit to Planner"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
